/**
 * English, and the source of truth.
 *
 * Every other bundle is a translation OF this file — so this one carries the
 * comments explaining what a string is for, and the others stay bare. When a key
 * is added here and nowhere else, `t()` falls back to this text rather than
 * rendering blank, so a half-translated release ships readable rather than
 * broken.
 *
 * Keys are grouped by screen, with `common` for the words that appear on more
 * than one. Nothing is reused across groups purely because the English happens
 * to match — "Cancel" the button and "Cancelled" the status translate
 * differently in Chinese, and sharing a key to save six characters is how that
 * mistake gets made.
 */
export default {
  common: {
    loading: "Loading…",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    close: "Close",
    back: "Back",
    copy: "Copy",
    copied: "Copied",
    all: "All",
    max: "Max",
    optional: "optional",
    somethingWrong: "Something went wrong. Please try again.",
    signIn: "Sign in",
    register: "Register",
    signOut: "Sign out",
  },

  nav: {
    lobby: "Games",
    money: "Money",
    promotions: "Promotions",
    vip: "VIP",
    rebate: "Rebate",
    invite: "Invite",
    winLoss: "Win / loss",
    account: "My account",
    language: "Language",
  },

  menu: {
    soon: "Soon",
    notSignedIn: "Not signed in",
    menu: "Menu",
    closeMenu: "Close menu",

    play: "Play",
    money: "Money",
    rewards: "Rewards",
    account: "Account",
    help: "Help",

    games: "Games",
    bonuses: "Bonuses",
    moneyPage: "Money",
    rebate: "Rebate rates",
    invite: "Invite friends",
    vip: "VIP",
    leaderboard: "Leaderboard",
    myAccount: "My account",
    winLoss: "Win / loss",
    howItWorks: "How it works",
    contact: "Help & contact",
  },

  footer: {
    help: "Help",
    legal: "Legal",
    terms: "Terms & conditions",
    privacy: "Privacy policy",
    deposits: "How deposits work",
    withdrawals: "How withdrawals work",
    responsible: "18+ only. Please play responsibly.",
  },

  authGate: {
    title: "Sign in to continue",
    body: "You can browse everything here. To play, deposit or claim anything, you need an account — it takes under a minute.",
    createAccount: "Create an account",
  },

  prompt: {
    titleAction: "Sign in to {action}",
    title: "Sign in to continue",
    body: "You need an account with {site} for that. It takes a minute, and you will come straight back here.",
    createAccount: "Create an account",
    haveOne: "I already have one",
    keepLooking: "Keep looking around",
    us: "us",
    playGame: "play {game}",
  },

  auth: {
    signInToPlay: "Sign in to play",
    username: "Username",
    password: "Password",
    tabUsername: "Username",
    tabEmail: "Email",
    tabPhone: "Phone",
    emailPlaceholder: "you@example.com",
    phonePlaceholder: "Your phone number",
    sessionExpired: "Your session expired. Please sign in again.",
    signingIn: "Signing in…",
    forgotPassword: "Forgot password?",
    createAccount: "Create an account",

    resetTitle: "Reset your password",
    resetSent: "If that address is registered, a code has been sent.",
    resetSentHint: "The code expires in 10 minutes and can be used once.",
    enterMyCode: "Enter my code",
    backToSignIn: "Back to sign in",
    email: "Email address",
    sending: "Sending…",
    sendResetCode: "Send reset code",

    newPasswordTitle: "Choose a new password",
    passwordUpdated: "Password updated. Taking you to sign in…",
    resetCode: "Reset code",
    resetCodeHint: "The 6-digit code we emailed you. It expires 10 minutes after it was sent.",
    newPassword: "New password",
    passwordRule: "At least 10 characters.",
    confirmPassword: "Confirm password",
    updating: "Updating…",
    updatePassword: "Update password",
    requestNewCode: "Request a new code",
  },

  account: {
    title: "My account",
    sectionAccount: "Account",
    sectionSecurity: "Security",
    sectionPreferences: "Preferences",

    verification: "Verification",
    verified: "Verified",
    accountsOnFile: "{count} bank account on file.",
    accountsOnFilePlural: "{count} bank accounts on file.",
    beingChecked: "Being checked",
    beingCheckedDetail: "Usually done within a few hours.",
    notAccepted: "Not accepted",
    notAcceptedDetail: "Check your details and try again.",
    notFinished: "Not finished",
    notFinishedDetail: "Your details are saved but you have not sent them to us yet.",
    notStarted: "Not started",
    notStartedDetail: "Needed before your first withdrawal, so we know where to pay you.",
    manageBanks: "Manage bank accounts",
    addBank: "Add your bank account",

    password: "Password",
    passwordIntro: "We ask for your current one even though you are signed in — a session is not proof of who is holding the phone.",
    currentPassword: "Current password",
    newPassword: "New password",
    newPasswordHint: "At least 10 characters. A short phrase you can remember beats a short jumble.",
    confirmNewPassword: "Confirm new password",
    changePassword: "Change password",

    currency: "Currency",
    currencyIntro: "Changeable only while your balance is empty and before your first transaction. After that it would change what your past statements mean, so live chat has to do it.",
    chooseCurrency: "Choose a currency",
    changeCurrency: "Change currency",
  },

  verifyEmail: {
    title: "Confirm your email address",
    sentTo: "We sent a 6-digit code to",
    enterIt: "Enter it to finish setting up your account.",
    checking: "Checking…",
    verify: "Verify",
    resending: "Sending…",
    resend: "Resend code",
    useDifferent: "Use a different account",
    expires: "The code expires in 10 minutes.",
  },

  register: {
    title: "Create your account",
    email: "Email address",
    emailHint: "We will send a 6-digit code to confirm it.",
    signInInstead: "Sign in instead",
    sending: "Sending…",
    sendCode: "Send code",

    enterCodeSentTo: "Enter the code we sent to",
    verificationCode: "Verification code",
    checking: "Checking…",
    continue: "Continue",
    useDifferentEmail: "Use a different email",
    resend: "Resend",

    username: "Username",
    usernameHint: "Letters, numbers and underscore.",
    password: "Password",
    passwordHint: "At least 10 characters.",
    confirmPassword: "Confirm password",
    fullName: "Full name",
    fullNameHint: "Must match your bank account name exactly — used for deposits and withdrawals.",
    nickname: "Nickname",
    nicknameHint: "What other players see.",
    phoneCode: "Code",
    phone: "Phone",
    creating: "Creating…",
    createAccount: "Create account",
    alreadyHave: "Already have an account?",

    needsMore: "Needs {count} more character.",
    needsMorePlural: "Needs {count} more characters.",
    passwordsDoNotMatch: "The passwords do not match.",
    nicknameRequired: "Choose a nickname — at least 3 letters or numbers.",
    phoneRequired: "Enter your phone number.",
    fullNameRequired: "Enter your full name as it appears on your bank account.",
  },

  bank: {
    title: "Your bank account",
    intro: "Withdrawals go to an account you have saved and we have checked — never one typed in at withdrawal time. That is what keeps your money reachable only by you.",
    confirmed: "Confirmed",
    weCanPayAny: "We can pay into any of these",
    wePayInto: "We pay into",
    pickAtWithdrawal: "You pick which one at withdrawal. All of them are in your own name — that is what we check before adding one.",
    checkingNew: "We are checking your new account.",
    carryOnMeantime: "Withdrawals carry on as normal to the accounts above in the meantime.",
    spottedMistake: "Spotted a mistake? Message live chat — a request cannot be edited once our team has it.",
    couldNotApprove: "We could not approve your last change.",
    maxAccounts: "You have saved the maximum of {count} accounts. Message live chat if you need to remove one.",
    tryAgain: "Try again",
    addAnother: "Add another bank account",

    bankLabel: "Bank",
    selectYourBank: "Select your bank",
    otherTypeBelow: "Other — type it below",
    bankNamePlaceholder: "Bank name",
    holderName: "Account holder name",
    accountNumber: "Account number",
    whyAdding: "Why are you adding this?",
    stillHasToBe: "Still has to be {name} — a different bank, not a different person.",
    sending: "Sending…",
    sendForChecking: "Send for checking",

    weAreChecking: "We are checking this",
    sentAt: "Sent {when}. Usually done within a few hours.",
    couldNotConfirm: "We could not confirm this",
    checkAndRetry: "Check your bank details and try again.",

    addYours: "Add your bank account",
    neededBefore: "Needed before your first withdrawal, so we know where to send your money. Just your bank details — no documents.",
    starting: "Starting…",
    start: "Start",

    yourDetails: "Your bank details",
    mustComeFrom: "Money must come from this account, and we pay back into it only.",
    hasToMatch: "Has to match the name on your account:",
    weWillSave: "We will save this name and check every payment in and out against it.",
    doesNotMatch: "This does not match the name on your account. Message live chat if your name has changed.",
    matches: "✓ Matches the name on your account.",
    finishLaterTitle: "Keep these details without submitting yet",
    finishLater: "Finish later",
  },

  misc: {
    siteUnavailable_title: "Site unavailable",
    verifyYourEmail: "Verify your email",
    reasonGiven: "Reason given",
    createAccount: "Create account",
    seeBonuses: "See bonuses",

    siteUnavailable: "This site is not currently available. Please check the address and try again.",
    notifications: "Notifications",
    needAttention: "{count} things need your attention",
    allCaughtUp: "You are all caught up.",
    qrAlt: "Deposit address as a QR code",
    qrTooLong: "This address is too long to show as a code — please copy it instead.",
    youSubmitted: "You submitted",
    enterCodeWeSent: "Enter the 6-digit code we sent you.",
    sentAgain: "Sent again",
    resend: "Resend",
  },

  support: {
    help: "Help",
    tabChat: "Chat",
    tabFaq: "FAQ",
    tabContact: "Contact",
    automatedNotice: "Automated answers. For anything to do with your own account or money, use WhatsApp under Contact — a person there can actually look.",
    openWhatsApp: "Open WhatsApp →",
    whatsappIntro: "WhatsApp is the quickest way to reach us, and the only one where we can look at your account.",
    demoNumber: "Demo number — no one is on the other end. Set a real one in the site's branding settings before launch.",
  },

  legal: {
    notRealTitle: "Not the real document.",
    notRealBody: "This is an outline of the sections {brand} needs, not reviewed legal text, and it is not binding on anyone. It has to be replaced with wording drafted for this operator, its licence and the markets it takes players from before the site trades.",
    englishOnly: "This draft is published in English only. It will be translated once the final wording is settled — a machine translation of an agreement is not an agreement.",
    version: "Version: unpublished draft. No effective date.",
    notFound: "Document not found.",
  },

  live: {
    title: "Live bets",
    playing: "{count} playing now",
    quiet: "Nobody is playing right now.",
    quietBody: "Bets appear here as they are settled.",
    stat_paid: "Paid out today",
    stat_biggest: "Biggest win today",
    stat_rounds: "Rounds today",
    topToday: "Biggest wins today",
    noWins: "No wins yet today.",
    won: "won",
    staked: "staked",
  },

  providers: {
    title: "Our game studios",
    subtitle: "Games supplied by licensed studios.",
  },

  trust: {
    licensed: "Licensed operator",
    licensedBody: "Operating under the licence shown in our terms.",
    payouts: "Same-day payouts",
    payoutsBody: "Checked by a person, usually within minutes.",
    support: "Support around the clock",
    supportBody: "Live chat, every day.",
    fair: "Provably settled",
    fairBody: "Every round is recorded and auditable.",
  },

  help: {
    title: "How it works",
    intro: "Short guides to the things people ask about most.",
    openIt: "Go there",
    stepsLabel: "{count} steps",

    deposit_title: "Adding money",
    deposit_body: "Open Money, pick what you are paying in, then how. You will see the account or wallet address to pay to, and a reference to quote. Press \"I have paid\" once you have sent it — that puts the deposit in our queue, it does not credit anything by itself. A person checks every payment, which is usually minutes. Your balance moves the moment it is confirmed.",
    deposit_note: "Money has to come from an account in your own name. A payment from somebody else cannot be credited.",

    withdraw_title: "Cashing out",
    withdraw_body: "Save a bank account first — we only pay into one you have saved and we have checked, never one typed in at the time. Open Money, choose Withdraw, enter an amount and confirm. The amount is set aside the moment you ask, so it leaves the balance you can play with straight away.",
    withdraw_note: "The name on the bank account must match the name on your account exactly. You can cancel a request from History while it is still waiting, and the money comes back.",

    bonus_title: "Bonus and cash",
    bonus_body: "Bonus credit is not yours to withdraw until you have played it through. Each bonus carries its own play-through requirement, fixed when it is given, and playing meets the oldest one first. When a bonus is fully played through it becomes cash and can be withdrawn like anything else.",
    bonus_note: "The Transfer tab shows how much is still to play through and what is ready to move.",

    rebate_title: "Rebate",
    rebate_body: "You earn a share of everything you stake, win or lose — not a share of what you lose. It is totalled once a day and credited to your bonus balance. The rate depends on the game supplier, and your VIP tier adds to it.",
    rebate_note: "The live rates for every supplier are on the Rebate page.",

    stillStuck: "Still stuck?",
    stillStuckBody: "Live chat is open around the clock, and can see your account.",
    contactUs: "Message us",
  },

  lobby: {
    categories: {
      slots: "Slots",
      live: "Live Casino",
      sports: "Sports",
      fishing: "Fishing",
      lottery: "4D Lottery",
      cockfight: "Cock Fight",
      horse: "Horse Racing",
      esports: "Esports",
    },

    banners: {
      welcomeTitle: "Welcome bonus",
      welcomeBody: "Up to 150% extra on your first deposit",
      cashbackTitle: "Daily cashback",
      cashbackBody: "A little back on every round you play",
      friendTitle: "Bring a friend",
      friendBody: "You both get a reward",
    },

    trust: {
      withdraw: "Withdraw",
      withdrawValue: "Same day",
      chat: "Live chat",
      chatValue: "Always on",
      payBy: "Pay by",
      payByValue: "Bank or USDT",
    },

    popular: "Popular",
    notConnected: "This game is not connected yet.",
    notConnectedTry: "This game is not connected yet. Please try another.",
    showSlide: "Show slide {number}",
    dismiss: "Dismiss",
    winningNow: "Winning now",
    pickAGame: "Pick a game",
    comingSoon: "Coming soon",
    showAll: "Show all",
    gamesOnTheWay: "Games on the way",
    hot: "HOT",
    gameStudios: "Game studios",
    bonuses: "Bonuses",
    viewAll: "View all",
    bonus: "Bonus",
  },

  vip: {
    title: "VIP",
    intro: "Your tier is set by everything you have ever deposited. It never goes down.",
    currentTier: "Current tier",
    depositedToDate: "Deposited to date",
    toReach: "To reach {tier}",
    topTier: "You are on the top tier.",
    allTiers: "All tiers",
    tier: "Tier",
    deposited: "Deposited",
    extraRebate: "Extra rebate",
    includes: "Includes",
    you: "You",
    orMore: "{amount} or more",
    notPaidTitle: "Tier rebate bonuses are not being paid yet.",
    notPaidBody: "Your tier and your deposits are tracked from now, and the standard rebate is paid as published. We will say so here when the tier bonus goes live.",
  },

  promotions: {
    title: "Promotions",
    intro: "Everything running on this site, with the full terms.",
    hideTerms: "Hide terms",
    readTerms: "Read the terms",
    draftTitle: "These terms are a draft.",
    draftBody: "The figures, dates and named suppliers above are placeholders and will be confirmed before any offer is advertised. The rules themselves — minimum payout, the VIP rate band and excluded categories — are live and paid as described.",
  },

  rebate: {
    title: "Rebate rates",
    intro: "Earned on every round you play, win or lose.",
    settingUp: "Rebate rates are being set up. They will appear here shortly.",
    toYou: "to you",
    referral: "Referral",
    shareholder: "Shareholder",
    supplier: "Supplier",
    you: "You",
    howItAddsUp: "How it adds up",
    example: "Rebate is a share of what you stake, not of what you lose.",
    exampleFull: "Rebate is a share of what you stake, not of what you lose. Stake {stake} on slots at {rate} and you earn {reward} back — whether those rounds won or lost.",
    exampleDaily: "It is totalled daily and credited to your bonus balance.",
  },

  invite: {
    title: "Invite friends",
    intro: "Share your code. Anyone who signs up through it is recorded as yours.",
    yourCode: "Your code",
    codeCopied: "Code copied",
    copyCode: "Copy code",
    yourLink: "Your link",
    joinedThroughYou: "Joined through you",
    namesNotShown: "Counted from sign-ups that used your code. We do not show you their accounts.",
    noRewardTitle: "No referral reward is being paid yet.",
    noRewardBody: "Sign-ups through your code are being recorded from now, so nothing is lost — but the rebate you earn today is on your own play only. We will say so here when that changes.",
  },

  winLoss: {
    title: "Win / loss",
    intro: "Settled rounds only. A bet still in play is not counted either way.",
    from: "From",
    to: "To",
    ranges: { today: "Today", week: "Last 7 days", month: "Last 30 days" },
    youAreUp: "You are up",
    youAreDown: "You are down",
    staked: "Staked",
    returned: "Returned",
    rounds: "Rounds",
    byGameType: "By game type",
    dayByDay: "Day by day",
    empty: "No settled rounds in this period.",
    roundsStaked: "{rounds} rounds · {staked} staked",
  },

  wallet: {
    title: "Wallet",
    systemBalance: "System balance",
    makeADeposit: "Make a deposit to get started.",
    setAside: "{amount} set aside",
    addMoney: "Add money",
    withdraw: "Withdraw",
    recentActivity: "Recent activity",
    empty: "Nothing here yet.",
  },

  money: {
    title: "Money",
    available: "Available",
    held: "Held",
    heldHint: "against a request being checked",
    bonus: "Bonus",
    bonusHint: "play it through before it can be withdrawn",

    tabs: {
      deposit: "Add money",
      transfer: "Transfer",
      withdraw: "Withdraw",
      history: "History",
      bank: "Bank",
    },

    hints: {
      transfer: "Move bonus to cash, or send to another player",
      history: "Every deposit, withdrawal and movement",
      bank: "The account your withdrawals are paid to",
    },
  },

  deposit: {
    noMethodsTitle: "No payment methods yet",
    noMethodsBody: "This site has not enabled a way to pay in. Please contact support to have credit added to your account.",

    ticketNumber: "Deposit #{id}",
    network: "Network",
    sendToAddress: "Send to this address",
    copyAddress: "Copy address",
    wrongNetwork: "Send only on {network}. Funds sent on another network cannot be recovered.",

    cardPendingTitle: "Card payments are still being connected.",
    cardPendingBody: "Our team will contact you to complete this deposit, or you can cancel it and pay another way.",

    transferTo: "Transfer {amount} to:",
    bank: "Bank",
    accountName: "Account name",
    accountNumber: "Account no.",
    swift: "SWIFT",
    branch: "Branch",
    copyAccount: "Copy account number",
    quoteReference: "Put {reference} in the transfer reference so it can be matched to your account.",
    transferToConfirmed: "Transfer {amount} to the account our team confirms with you.",
    quoteReferenceGeneric: "Quote reference {reference} on the transfer so it can be matched to your account.",

    expires: "This request expires {when}.",
    yourReference: "Your payment reference (optional)",
    yourReferencePlaceholder: "Transaction hash or bank reference",
    yourReferenceHint: "Speeds up confirmation — our team matches it against the payment.",
    iHavePaid: "I have paid",
    sending: "Sending…",

    checkingTitle: "Thanks — our team is checking your payment.",
    checkingBody: "Your balance updates as soon as it is confirmed. Nothing has been credited yet.",
    makeAnother: "Make another deposit",

    payIn: "Pay in",
    chooseHow: "Choose how to pay",
    recommended: "Recommended",
    selected: "Selected",
    min: "Min {amount}",
    max: "Max {amount}",
    addressLabel: "{network} address",

    amount: "Amount",
    otherAmount: "Other amount in {currency}",
    aboveMaximum: "The most this method takes at once is {amount}.",
    belowMinimum: "Minimum for this method is {amount}.",
    youWillGet: "You will receive about",
    rateIndicative: "Indicative. The rate applied is the one when your payment is confirmed.",
    rateStale: "This rate was last checked a while ago and may have moved.",
    rateUnavailable: "We could not fetch a {pay} rate just now. Your deposit will be converted to {account} at the rate when it is confirmed.",
    preparing: "Preparing…",
    submit: "Deposit {amount}",

    groups: { instant: "Instant", bank: "Bank & crypto" },
  },

  withdraw: {
    doneTitle: "Request received. That amount is now set aside and out of the balance you can play with.",

    blockers: {
      statusTitle: "Your account is on hold",
      statusBody: "Withdrawals are paused. Message live chat and we will sort it out.",
      kycTitle: "Tell us where to pay you",
      kycBody: "Save your bank account first — it takes a minute, and no documents are needed.",
      kycAction: "Add bank account",
      emailTitle: "Confirm your email first",
      emailBody: "Enter the 6-digit code we sent you.",
      balanceTitle: "Nothing to withdraw yet",
      balanceBody: "Money already set aside for a request you have made cannot be cashed out twice.",
      balanceAction: "Add money",
      dailyTitle: "That is all for today",
      dailyBody: "You have used all {count} withdrawal requests today. You can ask again tomorrow.",
      turnoverTitle: "Almost there",
      turnoverBody: "Play through {amount} more and withdrawals open up.",
      turnoverAction: "Play now",
    },

    sendAs: "Send it as",
    noMethods: "No withdrawal method is switched on yet. Message live chat and we will help.",
    today: "Withdrawals today",
    amount: "Amount",
    walletAddress: "Wallet address",
    walletAddressPlaceholder: "Your {network} address",
    walletAddressWarning: "Must be a {network} address. We cannot recover funds sent to the wrong network.",

    whichAccount: "Which account?",
    paidIntoSaved: "Paid into your saved bank account",
    noAccountSaved: "No bank account saved yet.",
    onlySavedAccounts: "Withdrawals only go to an account you have saved and we have checked.",
    addAnother: "Add another",
    ownName: "— it has to be in your own name.",

    holdTitle: "The money is set aside the moment you ask.",
    holdBody: "It leaves the balance you can play with straight away, and comes back only if you cancel or we cannot approve it. Cancel any time from History while it is still waiting.",
    holdTurnover: "You also need to have played through at least what you have put in before we can send a withdrawal.",

    playRequirement: "Play requirement",
    requesting: "Requesting…",
    submit: "Request withdrawal",
  },

  transfer: {
    cash: "Cash",
    setAside: "{amount} set aside",
    bonus: "Bonus",

    modes: { bonus: "Bonus → Cash", member: "Send to player" },

    bonusIntro: "Bonus credit is not yours to withdraw until you have played it through. Moving it here is the moment it becomes real money.",
    playedThrough: "Played through",
    amountToMove: "Amount to move",
    moving: "Moving…",
    moveToCash: "Move to cash",
    keepPlaying: "Keep playing to unlock this.",

    amount: "Amount",
    sendTo: "Send to",
    theirUsername: "Their username",
    note: "Note",
    checkUsernameTitle: "Check the username carefully.",
    checkUsernameBody: "A transfer lands straight in that player's balance and cannot be reversed.",
    transfersLeft: "You have {left} of {total} transfers left today.",
    send: "Send",
  },

  history: {
    show: "Show",
    everything: "Everything",
    moneyIn: "Money in",
    moneyOut: "Money out",
    from: "From",
    to: "To",

    ranges: {
      today: "Today",
      yesterday: "Yesterday",
      week: "Last 7 days",
      month: "Last 30 days",
    },

    empty: "Nothing in this period. Try a wider date range.",
    cancelHold: "Cancel and release the hold",
    cancelling: "Cancelling…",
    requested: "{amount} requested",
    shortBy: "short by",
    overBy: "over by",

    kind: { deposit: "Deposit", withdrawal: "Withdrawal" },

    status: {
      AwaitingConfirmation: "Awaiting confirmation",
      Pending: "Pending",
      Completed: "Completed",
      Rejected: "Rejected",
      Cancelled: "Cancelled",
      Expired: "Expired",
      Approved: "Approved",
      Processing: "Processing",
    },
  },
};
